import { Switch, Typography, TextField, Tooltip, Alert } from '@mui/material';
import BasicSelect from 'further-ui/components/BasicSelect';
import useFeeTemplateOptions from 'hooks/lp/data/fee-manager/useFeeTemplateOptions';
import RadioButtons from 'further-ui/components/RadioButtons';
import { CurrencyCode, CommitmentType } from 'further-types/lp/vehicle';
import { CapitalContributionType } from 'further-types/lp/carry-settings';
import { Controller, useForm } from 'react-hook-form';
import { carrySettings } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCarrySettingsContext } from './CarrySettingsContext';
import FormField from 'further-ui/components/FormField';
import NumberInput from 'further-ui/components/NumberInput';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getCurrencySymbol, numberToCurrencyString } from 'further-ui/utils';
import { round } from 'lodash';
import { usePagination } from 'further-ui/hooks';
import { SortOrder } from 'further-types/utils';
import { Column } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';

type FormValues = z.infer<typeof carrySettings.CarriedInterestPartnerSettings>;

type Props = {
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    commitmentType?: CommitmentType;
    fixedCapitalAmount?: number | null;
    carrySettings: {
      carryPartnerName?: string | null;
      carryPartnerFundCommitment?: number;
      capitalContributionType?: CapitalContributionType | null;
      capitalContributionAmountLcy?: number | null;
      capitalContributionPercentage?: number | null;
      loanCommitmentLcy?: number | null;
      excludeCarryPartnerFromAllocations?: boolean;
      carryPartnerLpFeeTemplateId?: string | null;
    };
  };
};

const calculateCapitalContributionAmountLcy = (
  vehicleFixedCapital: number,
  capitalContributionPercentage: number,
) => {
  return round((vehicleFixedCapital * capitalContributionPercentage) / 100, 2);
};

const CarriedInterestPartnerSettings: React.FC<Props> = ({ vehicle }) => {
  const pagination = usePagination({
    id: 'carried-interest-partner-settings',
    orderBy: 'commitmentDate',
    order: SortOrder.Desc,
  });

  const {
    data: commitmentsData,
    isLoading: commitmentsLoading,
    isError: commitmentsError,
  } = api.commitments.list.useQuery({
    vehicleId: vehicle?._id ?? '',
    onlyEqualised: true,
    ...pagination.properties,
  });

  const isCarryPartnerEqualised =
    commitmentsData?.results?.some(
      (commitment) => commitment.isCarryPartnerCommitment,
    ) ?? false;

  const isCapitalAndLoan =
    vehicle.commitmentType === CommitmentType.CapitalAndLoan;

  const vehicleFixedCapital = vehicle.fixedCapitalAmount;
  const initialCapitalContributionAmountLcy =
    vehicle.carrySettings.capitalContributionType ===
      CapitalContributionType.PercentageOfTotalCapital &&
    vehicleFixedCapital != null &&
    vehicle.carrySettings.capitalContributionPercentage != null
      ? calculateCapitalContributionAmountLcy(
          vehicleFixedCapital,
          vehicle.carrySettings.capitalContributionPercentage,
        )
      : vehicle.carrySettings.capitalContributionAmountLcy ?? null;

  const lpFeeTemplateOptions = useFeeTemplateOptions(vehicle._id);

  const { control, handleSubmit, setValue, watch } = useForm({
    resolver: zodResolver(carrySettings.CarriedInterestPartnerSettings),
    shouldFocusError: true,
    values: {
      carryPartnerName:
        vehicle.carrySettings.carryPartnerName ?? 'Carry partner',
      excludeCarryPartnerFromAllocations:
        vehicle.carrySettings.excludeCarryPartnerFromAllocations ?? false,
      lpFeeTemplateId:
        vehicle.carrySettings.carryPartnerLpFeeTemplateId ?? 'none',
      ...(isCapitalAndLoan
        ? {
            capitalContributionType:
              vehicle.carrySettings.capitalContributionType ??
              CapitalContributionType.FixedAmount,
            capitalContributionAmountLcy: initialCapitalContributionAmountLcy,
            capitalContributionPercentage:
              vehicle.carrySettings.capitalContributionPercentage ?? null,
            loanCommitmentLcy: vehicle.carrySettings.loanCommitmentLcy ?? null,
            carryPartnerFundCommitment: 0,
          }
        : {
            capitalContributionType: null,
            capitalContributionAmountLcy: null,
            capitalContributionPercentage: null,
            loanCommitmentLcy: null,
            carryPartnerFundCommitment:
              vehicle.carrySettings.carryPartnerFundCommitment ?? 0,
          }),
    },
    criteriaMode: 'all',
  });

  const { setSaveButtonPending } = useCarrySettingsContext();
  const options = useHandleTrpcMutation()({
    successMessage: 'Carried interest partner settings updated.',
    onMutate: () => setSaveButtonPending(true),
    onSettled: () => setSaveButtonPending(false),
  });

  const { mutate: updateCarriedInterestPartnerSettings } =
    api.vehicle.updateCarriedInterestPartnerSettings.useMutation(options);

  const onSubmit = (data: FormValues) => {
    if (commitmentsLoading || commitmentsError || !commitmentsData) return;
    const dataToSubmit = isCarryPartnerEqualised
      ? {
          carryPartnerName: data.carryPartnerName,
          excludeCarryPartnerFromAllocations:
            data.excludeCarryPartnerFromAllocations,
          lpFeeTemplateId:
            data.lpFeeTemplateId === 'none' ? null : data.lpFeeTemplateId,
        }
      : {
          ...data,
          lpFeeTemplateId:
            data.lpFeeTemplateId === 'none' ? null : data.lpFeeTemplateId,
          ...(data.capitalContributionType ===
          CapitalContributionType.FixedAmount
            ? {
                capitalContributionPercentage: null,
              }
            : {
                capitalContributionAmountLcy: null,
              }),
        };

    updateCarriedInterestPartnerSettings({
      vehicleId: vehicle._id,
      ...dataToSubmit,
    });
  };

  const currencySymbol = getCurrencySymbol(vehicle.baseCurrency);
  const capitalContributionType = watch('capitalContributionType');
  const capitalContributionAmountLcy = watch('capitalContributionAmountLcy');
  const capitalContributionPercentage = watch('capitalContributionPercentage');
  const loanCommitmentLcy = watch('loanCommitmentLcy');

  const totalCommitment =
    capitalContributionType ===
      CapitalContributionType.PercentageOfTotalCapital && !vehicleFixedCapital
      ? 'TBC'
      : numberToCurrencyString(
          (capitalContributionAmountLcy ?? 0) + (loanCommitmentLcy ?? 0),
          {
            currency: vehicle.baseCurrency,
          },
        );

  const equalisedTooltipText =
    "As the fund has completed its first close, the carry partner's total commitment can only be adjusted in the Close & Equalise feature when processing a subsequent close.";

  if (commitmentsError) {
    return (
      <Alert severity="error">
        Failed to load commitment data. Please refresh the page before making
        changes.
      </Alert>
    );
  }

  return (
    <form id="carry-settings-form-partners" onSubmit={handleSubmit(onSubmit)}>
      <Column spacing="lg">
        <FormField size="md" label="Carry partner name">
          <Controller
            name="carryPartnerName"
            control={control}
            render={({ fieldState: { error }, field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder="Enter carry partner name"
                error={!!error}
                helperText={error?.message}
                size="small"
              />
            )}
          />
        </FormField>
        {isCapitalAndLoan ? (
          <>
            <FormField
              size="md"
              label="Capital contribution type"
              labelColorLight={isCarryPartnerEqualised}
              tooltip={
                !isCarryPartnerEqualised
                  ? `Enter a fixed sum or percentage of total fund capital. Where a percentage is used, the amount of capital in ${currencySymbol} will change automatically if total fund capital changes.`
                  : undefined
              }
            >
              {isCarryPartnerEqualised ? (
                <Typography variant="h6">
                  {capitalContributionType ===
                  CapitalContributionType.FixedAmount
                    ? 'Fixed amount'
                    : 'Percentage of total capital'}
                </Typography>
              ) : (
                <Controller
                  name="capitalContributionType"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <RadioButtons
                      value={value}
                      direction="row"
                      options={[
                        {
                          value: CapitalContributionType.FixedAmount,
                          label: 'Fixed amount',
                        },
                        {
                          value:
                            CapitalContributionType.PercentageOfTotalCapital,
                          label: 'Percentage of total capital',
                        },
                      ]}
                      onChange={(val) => {
                        const nextType =
                          val ===
                          CapitalContributionType.PercentageOfTotalCapital
                            ? CapitalContributionType.PercentageOfTotalCapital
                            : CapitalContributionType.FixedAmount;

                        onChange(nextType);

                        if (
                          nextType ===
                            CapitalContributionType.PercentageOfTotalCapital &&
                          vehicleFixedCapital != null &&
                          capitalContributionPercentage != null
                        ) {
                          setValue(
                            'capitalContributionAmountLcy',
                            calculateCapitalContributionAmountLcy(
                              vehicleFixedCapital,
                              capitalContributionPercentage,
                            ),
                          );
                        }
                      }}
                    />
                  )}
                />
              )}
            </FormField>

            {capitalContributionType ===
              CapitalContributionType.FixedAmount && (
              <FormField size="md" label="Capital contribution amount">
                <Controller
                  name="capitalContributionAmountLcy"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => {
                    const input = (
                      <NumberInput
                        value={value ?? undefined}
                        fullWidth
                        onChange={onChange}
                        placeholder="Enter amount"
                        min={0}
                        decimals={2}
                        helperText={error?.message?.toString()}
                        error={!!error}
                        currencyStartAdornment
                        currency={vehicle.baseCurrency}
                        disabled={isCarryPartnerEqualised}
                      />
                    );

                    return isCarryPartnerEqualised ? (
                      <Tooltip title={equalisedTooltipText}>
                        <span>{input}</span>
                      </Tooltip>
                    ) : (
                      input
                    );
                  }}
                />
              </FormField>
            )}
            {capitalContributionType ===
              CapitalContributionType.PercentageOfTotalCapital && (
              <>
                <FormField size="md" label="Capital contribution amount (%)">
                  <Controller
                    name="capitalContributionPercentage"
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => {
                      const input = (
                        <NumberInput
                          value={value ?? undefined}
                          placeholder="Enter amount (%)"
                          onChange={(nextPercentage) => {
                            onChange(nextPercentage);

                            if (
                              vehicleFixedCapital != null &&
                              nextPercentage != null
                            ) {
                              setValue(
                                'capitalContributionAmountLcy',
                                calculateCapitalContributionAmountLcy(
                                  vehicleFixedCapital,
                                  nextPercentage,
                                ),
                              );
                            }
                          }}
                          min={0}
                          max={100}
                          fullWidth
                          decimals={10}
                          helperText={error?.message?.toString()}
                          error={!!error}
                          showPercentSuffix
                          disabled={isCarryPartnerEqualised}
                        />
                      );

                      return isCarryPartnerEqualised ? (
                        <Tooltip title={equalisedTooltipText}>
                          <span>{input}</span>
                        </Tooltip>
                      ) : (
                        input
                      );
                    }}
                  />
                </FormField>
                <FormField
                  size="md"
                  label="Capital contribution amount"
                  labelColorLight={!vehicleFixedCapital}
                  tooltip={
                    vehicleFixedCapital
                      ? undefined
                      : 'The exact capital contribution amount will be calculated as part of each fund close and will display here after the first close. It is subject to change until the final fund close.'
                  }
                >
                  {vehicleFixedCapital ? (
                    <Controller
                      name="capitalContributionAmountLcy"
                      control={control}
                      render={({ field: { value }, fieldState: { error } }) => (
                        <NumberInput
                          value={value ?? undefined}
                          placeholder="Enter amount"
                          onChange={() => {}}
                          fullWidth
                          min={0}
                          decimals={2}
                          helperText={error?.message?.toString()}
                          error={!!error}
                          currencyStartAdornment
                          currency={vehicle.baseCurrency}
                          disabled
                        />
                      )}
                    />
                  ) : (
                    <Typography variant="h6">{totalCommitment}</Typography>
                  )}
                </FormField>
              </>
            )}

            <FormField size="md" label="Loan commitment">
              <Controller
                name="loanCommitmentLcy"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  const input = (
                    <NumberInput
                      value={value ?? undefined}
                      onChange={onChange}
                      placeholder="Enter commitment"
                      min={0}
                      fullWidth
                      decimals={2}
                      helperText={error?.message?.toString()}
                      error={!!error}
                      currencyStartAdornment
                      currency={vehicle.baseCurrency}
                      disabled={isCarryPartnerEqualised}
                    />
                  );

                  return isCarryPartnerEqualised ? (
                    <Tooltip title={equalisedTooltipText}>
                      <span>{input}</span>
                    </Tooltip>
                  ) : (
                    input
                  );
                }}
              />
            </FormField>

            <StaticField
              label="Carry partner total commitment"
              tooltip={
                !vehicleFixedCapital
                  ? 'The exact total commitment will be calculated as part of each fund close and will display here after the first close. It is subject to change until the final fund close.'
                  : undefined
              }
            >
              {totalCommitment}
            </StaticField>
          </>
        ) : (
          <FormField size="md" label="Carry partner commitment">
            <Controller
              name="carryPartnerFundCommitment"
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, ...rest },
              }) => {
                const input = (
                  <NumberInput
                    {...rest}
                    decimals={2}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    placeholder="Enter commitment"
                    currencyStartAdornment
                    clearZeroValueOnFocus
                    currency={vehicle?.baseCurrency}
                    disabled={isCarryPartnerEqualised}
                  />
                );

                return isCarryPartnerEqualised ? (
                  <Tooltip title={equalisedTooltipText}>
                    <span>{input}</span>
                  </Tooltip>
                ) : (
                  input
                );
              }}
            />
          </FormField>
        )}
        <FormField size="md" label="Management fee template">
          <Controller
            name="lpFeeTemplateId"
            control={control}
            render={({ field }) => (
              <BasicSelect
                fullWidth
                size="small"
                value={field.value ?? 'none'}
                onChange={field.onChange}
                options={lpFeeTemplateOptions}
                placeholder="Select template"
              />
            )}
          />
        </FormField>
        <FormField
          size="md"
          label="Exclude carry partner from vehicle allocations"
          tooltip="Turn this on when the carry partner entity's fund commitment is to be excluded from the vehicle's total commitment and all transaction allocations."
        >
          <Controller
            name="excludeCarryPartnerFromAllocations"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Switch
                edge="start"
                checked={value ?? false}
                onChange={(e) => onChange(e.target.checked)}
              />
            )}
          />
        </FormField>
      </Column>
    </form>
  );
};

export default CarriedInterestPartnerSettings;
